import { useFormContext, Controller } from "react-hook-form";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/Input";
import { Surface } from "@core/components/ui/surface";
import { H3 } from "@core/components/ui/typography";
import { Checkbox } from "@core/components/ui/checkbox";
import { Textarea } from "@core/components/ui/textarea";
import { SettingModel } from "@/apidtos";
import { formatName } from "@core/helpers";
import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@core/components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-light-svg-icons";

const isPlainObject = (v: any) => v && typeof v === "object" && !Array.isArray(v);
const inferIsNumberMap = (obj: Record<string, any>) => {
  const first = obj ? Object.values(obj)[0] : undefined;
  if (typeof first === "number") { return true; }
  return false;
};


export const EditorSection = (props: { setting: SettingModel; path?: string }) => {
  const { setting, path: parentPath } = props;
  const { register, control, watch } = useFormContext();
  const path = parentPath ? `${parentPath}.${setting.Name}` : setting.Name;
  const label = (
    <label htmlFor={path} className="text-sm font-medium flex items-center gap-1">
      {formatName(setting.Name)}
      {setting.Description && (
        <span className="group relative">
          <FontAwesomeIcon icon={faCircleInfo} className="text-blue-500 text-xs cursor-help" />
          <span className="absolute left-0 top-5 hidden group-hover:block bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-xs rounded py-1 px-2 whitespace-nowrap z-10 shadow-lg">
            {setting.Description}
          </span>
        </span>
      )}
    </label>
  );
  
  // Determine grid column span
  const fieldName = setting.Name?.toLowerCase() || '';
  const isNumeric = ['Int16', 'Int32', 'Int64', 'UInt16', 'UInt32', 'UInt64', 'Decimal', 'Float', 'Double'].includes(setting.Type);
  const isTextarea = setting.InputType === 'textarea';
  const isShortField = /phone|fax|zip|code|alpha/.test(fieldName) || isNumeric;
  const isWideField = /description|note|comment|address|street/.test(fieldName) || isTextarea;
  
  let colSpan = '';
  if (setting.IsArray || setting.Properties?.length) colSpan = 'col-span-full';
  else if (isWideField) colSpan = 'col-span-full';
  else if (isShortField) colSpan = 'col-span-1';
  
  if (setting.IsArray) {
    const currentVal = watch(path);
    if (isPlainObject(currentVal)) {
      return <div className={colSpan}><DictionaryEditor path={path} name={setting.Name} /></div>;
    }
    return <div className={colSpan}><ArrayEditor setting={setting} path={path} /></div>;
  }
  if (setting.Properties?.length) {
    return (
      <div className={colSpan}>
        <div className="border border-neutral-200 dark:border-neutral-700 rounded-md p-3 bg-neutral-50/50 dark:bg-neutral-900/50">
          <H3 className="text-base mb-2 flex items-center gap-1">
            {formatName(setting.Name)}
            {setting.Description && (
              <span className="group relative">
                <FontAwesomeIcon icon={faCircleInfo} className="text-blue-500 text-sm cursor-help" />
                <span className="absolute left-0 top-6 hidden group-hover:block bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-xs rounded py-1 px-2 whitespace-nowrap z-10 shadow-lg max-w-xs">
                  {setting.Description}
                </span>
              </span>
            )}
          </H3>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x-2 gap-y-1">
            {setting.Properties.map(p => (
              <EditorSection key={p.Name} setting={p} path={path} />
            ))}
          </div>
        </div>
      </div>
    );
  }
  const typeToUse = setting.Type === "File" ? "UInt32" : setting.Type;
  if (typeToUse === "String" && setting.InputType === "textarea") {
    switch (setting.InputType) {
      default:
      case "textarea":
        return (
          <div className={colSpan}>
            {label}
            <Textarea id={path} {...register(path)} className="h-20" />
          </div>
        );
    }
  }
  if (setting.Options?.length) {
    return (
      <div className={colSpan}>
        {label}
        <Controller
          name={path}
          control={control}
          render={({ field }) => (
            <Select
              value={field.value?.toString() ?? ""}
              onValueChange={field.onChange}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {setting.Options.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
    );
  }
  switch (typeToUse) {
    case "Boolean":
      return (
        <div className={colSpan}>
          {label}
          <div className="flex items-center h-9 px-3 rounded-[var(--form-radius)] border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
            <Controller
              name={path}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox id={path} checked={field.value} onCheckedChange={field.onChange} />
                  <span className="text-sm">{field.value ? 'True' : 'False'}</span>
                </label>
              )}
            />
          </div>
        </div>
      );
    case "String":
      return (
        <div className={colSpan}>
          {label}
          <Input id={path} {...register(path)} className="h-9" />
        </div>
      );
    case "Int16":
    case "Int32":
    case "Int64":
      return (
        <div className={colSpan}>
          {label}
          <Input type="number" id={path} {...register(path, { valueAsNumber: true })} className="h-9" />
        </div>
      );
    case "UInt16":
    case "UInt32":
    case "UInt64":
      return (
        <div className={colSpan}>
          {label}
          <Input type="number" id={path} min={0} {...register(path, { valueAsNumber: true })} className="h-9" />
        </div>
      );
    case "Decimal":
    case "Float":
    case "Double":
      return (
        <div className={colSpan}>
          {label}
          <Input type="number" id={path} step={0.01} {...register(path, { valueAsNumber: true })} className="h-9" />
        </div>
      );
    case "File":
      return (
        <div className={colSpan}>
          {label}
          <Button>Select your file</Button>
        </div>
      );
    default:
      console.warn("Unsupported setting type for editor: ", typeToUse);
      return null;
  }
  return <ArrayEditor setting={setting} path={path} />;
};

export const ArrayEditor = (props: { setting: SettingModel; path: string }) => {
  const { setting, path } = props;
  const { watch, setValue } = useFormContext();
  const currentArray = watch(path) as any[];
  console.log(`[ArrayEditor] path="${path}" currentArray:`, JSON.stringify(currentArray), "isArray:", Array.isArray(currentArray), "length:", Array.isArray(currentArray) ? currentArray.length : 'N/A');
  const defaultEntry = setting.Properties?.length ? {} : null;
  const isSimpleArray = !setting.Properties?.length;
  const [newValue, setNewValue] = useState('');

  const addItem = () => {
    if (!newValue.trim()) return;
    const next = newValue.trim();
    const existing = Array.isArray(currentArray) ? currentArray : [];
    // Avoid duplicate entries (common for role allow-lists).
    if (existing.some((v) => String(v ?? "").toLowerCase() === next.toLowerCase())) {
      setNewValue('');
      return;
    }
    setValue(path, [...existing, next], { shouldDirty: true });
    setNewValue('');
  };

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between py-1">
        <H3 className="mb-0 text-sm font-semibold flex items-center gap-1">
          {formatName(setting.Name)}
          {setting.Description && (
            <span className="group relative">
              <FontAwesomeIcon icon={faCircleInfo} className="text-blue-500 text-xs cursor-help" />
              <span className="absolute left-0 top-5 hidden group-hover:block bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-xs rounded py-1 px-2 whitespace-nowrap z-10 shadow-lg max-w-xs">
                {setting.Description}
              </span>
            </span>
          )}
        </H3>
        {!isSimpleArray && (
          <Button size="sm" variant="success" onClick={() => {
            const updated = Array.isArray(currentArray) ? [...currentArray, defaultEntry] : [defaultEntry];
            setValue(path, updated, { shouldDirty: true });
          }} className="h-6 px-2 text-xs">+ Add</Button>
        )}
      </div>
      
      {Array.isArray(currentArray) && currentArray.length > 0 && !isSimpleArray && (
        <div className="border rounded overflow-hidden">
          <div className="space-y-1 p-1">
            {currentArray.map((_, i) => (
              <InnerArrayEditor key={i} idx={i} setting={setting} path={path} />
            ))}
          </div>
        </div>
      )}

      {isSimpleArray && (
        <div className="space-y-2">
          {Array.isArray(currentArray) && currentArray.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {currentArray.map((_, i) => (
                <InnerArrayEditor key={i} idx={i} setting={setting} path={path} />
              ))}
            </div>
          )}
          <div className="flex gap-1">
            {setting.Options?.length ? (
              <Select
                value=""
                onValueChange={(v) => {
                  if (!v) return;
                  const existing = Array.isArray(currentArray) ? currentArray : [];
                  if (existing.some((x) => String(x ?? "").toLowerCase() === v.toLowerCase())) return;
                  setValue(path, [...existing, v], { shouldDirty: true });
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder={`Add ${formatName(setting.Name)}`} />
                </SelectTrigger>
                <SelectContent>
                  {setting.Options
                    .filter((opt) => !(Array.isArray(currentArray) && currentArray.some((x) => String(x ?? "").toLowerCase() === opt.toLowerCase())))
                    .map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : (
              <>
                <Input 
                  placeholder={`Add ${formatName(setting.Name)}`}
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
                  className="h-8 text-sm"
                />
                <Button size="sm" variant="success" onClick={addItem} className="h-8 px-3">Add</Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const InnerArrayEditor = (props: { setting: SettingModel; path: string; idx: number }) => {
  const { setting, path, idx } = props;
  const { control, register, watch, setValue } = useFormContext();
  const arrPath = `${path}.${idx}`;
  const currentArray = watch(path) as any[];
  const isSimpleArray = !setting.Properties?.length;

  const deleteEntry = () => {
    const updated = currentArray.filter((_, j) => j !== idx);
    setValue(path, updated, { shouldDirty: true });
  };

  if (!isSimpleArray) {
    console.log('SettingsComponents.tsx InnerArrayEditor rendering complex object:', setting.Name, setting.Properties);
    return (
      <div className="rounded border p-1.5">
        <div className="flex gap-2">
          <div className="grow grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-x-2 gap-y-1">
            {setting.Properties?.map(s => {
              // Determine width span based on field type and name
              const isNumeric = ['Int16', 'Int32', 'Int64', 'UInt16', 'UInt32', 'UInt64', 'Decimal', 'Float', 'Double'].includes(s.Type);
              const isBoolean = s.Type === 'Boolean';
              const isTextarea = s.InputType === 'textarea';
              const fieldName = s.Name?.toLowerCase() || '';
              
              // Short fields: phone, fax, zip, code, etc
              const isShortField = /phone|fax|zip|code|alpha|numeric/.test(fieldName);
              
              let colSpan = 'col-span-1';
              if (isTextarea) colSpan = 'col-span-full';
              else if (isBoolean) colSpan = 'col-span-1';
              else if (isShortField || isNumeric) colSpan = 'col-span-1';
              else if (/street|address|display/.test(fieldName)) colSpan = 'col-span-2';
              
              return (
                <div key={s.Name} className={colSpan}>
                  <label className="text-[10px] font-medium text-muted-foreground block mb-0.5 truncate" title={formatName(s.Name)}>
                    {formatName(s.Name)}
                  </label>
                  <EditorSection setting={s} path={arrPath} />
                </div>
              );
            })}
          </div>
          <Button size="sm" variant="danger" onClick={deleteEntry} className="self-start h-6 w-6 p-0 shrink-0">×</Button>
        </div>
      </div>
    );
  }

  const typeToUse = setting.Type === "File" ? "UInt32" : setting.Type;
  
  const renderInput = () => {
    if (typeToUse === "String" && setting.InputType === "textarea") {
      return <Textarea id={arrPath} {...register(arrPath)} className="h-20" />;
    }
    if (setting.Options?.length) {
      return (
        <Controller
          name={arrPath}
          control={control}
          render={({ field }) => (
            <Select
              value={field.value?.toString() ?? ""}
              onValueChange={field.onChange}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {setting.Options.map((opt) => (
                  <SelectItem value={opt} key={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      );
    }
    switch(typeToUse) {
      case "Boolean":
        return (
          <Controller
            name={arrPath}
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        );
      case "String":
        // For simple string arrays (no options), render as badge
        return watch(arrPath) || '';
      case "Int16":
      case "Int32":
      case "Int64":
        return watch(arrPath)?.toString() || '';
      case "UInt16":
      case "UInt32":
      case "UInt64":
        return watch(arrPath)?.toString() || '';
      case "Decimal":
      case "Float":
      case "Double":
        return watch(arrPath)?.toString() || '';
      case "File":
        return <Button size="sm">Select file</Button>;
      default:
        return null;
    }
  };

  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 rounded-full text-sm border border-blue-200 dark:border-blue-800">
      <span>{renderInput()}</span>
      <button 
        type="button"
        onClick={deleteEntry} 
        className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold">
        ×
      </button>
    </div>
  );
};

export const DictionaryEditor = (props: { path: string; name: string }) => {
  const { path, name } = props;
  const { register, setValue, watch } = useFormContext();
  const [newKey, setNewKey] = useState("");
  const value = watch(path) as Record<string, any> | undefined;
  const entries = value ? Object.entries(value) : [];
  const addEntry = () => {
    if (!newKey) { return; }
    const updated = { ...(value ?? {}) };
    updated[newKey] = {};
    setValue(path, updated, { shouldDirty: true });
    setNewKey("");
  };
  const deleteKey = (k: string) => {
    const updated = { ...(value ?? {}) };
    delete updated[k];
    setValue(path, updated, { shouldDirty: true });
  };
  const renderObject = (basePath: string, obj: any) => {
    if (!isPlainObject(obj)) {
      const regOpts = typeof obj === "number" ? { valueAsNumber: true } : undefined;
      return (
        <div className="flex">
          <Input id={basePath} {...register(basePath, regOpts as any)} />
        </div>
      );
    }
    const isNumberMap = inferIsNumberMap(obj);
    if (isNumberMap) {
      return (
        <Surface className="space-y-2">
          {Object.entries(obj).map(([k, v]) => (
            <div key={k} className="flex">
              <label htmlFor={`${basePath}.${k}`} className="me-2">{k}</label>
              <Input type="number" step={0.01} id={`${basePath}.${k}`} {...register(`${basePath}.${k}`, { valueAsNumber: true })} />
            </div>
          ))}
        </Surface>
      );
    }
    return (
      <Surface className="space-y-2">
        {Object.entries(obj).map(([k, v]) => (
          <div key={k}>
            <H3>{formatName(k)}</H3>
            {renderObject(`${basePath}.${k}`, v)}
          </div>
        ))}
      </Surface>
    );
  };
  return (
    <Surface className="space-y-2">
      <H3>{formatName(name)}</H3>
      {entries.map(([k, v]) => (
        <div key={k} className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">{k}</label>
            <Button variant="danger" onClick={() => deleteKey(k)}>Delete</Button>
          </div>
          {renderObject(`${path}.${k}`, v)}
        </div>
      ))}
      <div className="flex gap-2">
        <Input placeholder="key" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
        <Button variant="success" onClick={addEntry}>Add</Button>
      </div>
    </Surface>
  );
};